import React from 'react'

import { ArrowLeftOutlined } from '@ant-design/icons'
import { Alert, Button, Space, Statistic, Typography } from 'shared/ui'
import styled from 'styled-components'

import { CustomOTPInput } from './CustomOTPInput'

const { Title, Text } = Typography

interface EmailVerificationProps {
  email: string
  code: string
  onCodeChange: (value: string) => void
  onCodeSubmit: () => void
  onResendCode: () => void
  onBackToSignup: () => void
  timeLeft: number
  canResend: boolean
  error?: string
  loading?: boolean
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  code,
  onCodeChange,
  onCodeSubmit,
  onResendCode,
  onBackToSignup,
  timeLeft,
  canResend,
  error,
  loading = false
}) => {
  const isValidCode = code.length === 6

  // Обработчик изменения кода с фильтрацией только цифр
  const handleCodeChange = (value: string) => {
    // Дополнительная фильтрация только цифр
    const digitsOnly = value.replace(/[^0-9]/g, '')
    onCodeChange(digitsOnly)
  }

  return (
    <Space direction="vertical"
      size="large"
      style={{ width: '100%' }}
    >
      {/* Back Button */}
      <SBackButton 
        type="text" 
        icon={<ArrowLeftOutlined />} 
        onClick={onBackToSignup}
        size="small"
      >
        Back to sign up
      </SBackButton>
      
      <SVerificationHeader>
        <Title>
          Verify email
        </Title>

        <Text style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: '16px', display: 'block' }}>
          Code sent to  
          {' '}

          <span style={{  fontSize: '16px', color: 'var(--color-primary,  #8747DF)' }}>
            {email}
          </span>
        </Text>
      </SVerificationHeader>

      {/* Custom OTP Input */}
      <SOTPContainer>
        <CustomOTPInput
          value={code}
          onChange={handleCodeChange}
          disabled={loading}
          error={!!error}
        />
      </SOTPContainer>

      {/* Ошибка */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ textAlign: 'left' }}
        />
      )}

      {/* Confirm Button */}
      <Button 
        type="primary"
        size="large"
        block
        disabled={!isValidCode || loading}
        loading={loading}
        onClick={onCodeSubmit}
      >
        Verify
      </Button>

      {/* Timer and Resend */}
      <SResendSection>
        {canResend ? (
          <Button 
            type="link" 
            onClick={onResendCode}
            disabled={loading}
            style={{ padding: 0, color: 'var(--color-primary,  #8747DF)' }}
          >
            Resend code
          </Button>
        ) : (
          <Space>
            <Text style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: '14px' }}>
              You can resend code in
            </Text>

            <Statistic.Countdown
              value={Date.now() + timeLeft * 1000}
              format="mm:ss"
              valueStyle={{ 
                fontSize: '14px', 
                fontWeight: 600,
                color: 'var(--color-primary,  #8747DF)'
              }}
            />
          </Space>
        )}
      </SResendSection>
    </Space>
  )
}

const SBackButton = styled(Button)`
  align-self: flex-start;
  padding-left: 0;
  color: var(--color-text-secondary, #666);
  
  &:hover {
    color: var(--color-primary, #1890ff);
  }
`

const SVerificationHeader = styled.div`
  text-align: center;
  margin-bottom: 8px;
`

const SOTPContainer = styled.div`
  display: flex;
  justify-content: center;
`

const SResendSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: 8px;
` 